import { ArrayUtils } from '@shopping/common'
import { StateKey } from 'common/utils/flipState'
import { CommonState } from 'common/types'
import { EventDetailParam, EventDetailState } from 'common/types/entities/event'
import {
  fetchCollections,
  fetchProductsByIds,
  fetchSpecialEvent,
  fetchSpecialEvents,
} from 'common/services/specialEvent'
import { createCacheParam, isCacheHit } from 'common/utils/reduxCache'
import { ProductParam } from 'common/types/params'
import { fetchZzimedProducts, setProductZzim } from 'common/services/zzim'
import { ZzimProducts } from 'common/types/entities/api/zzim'
import HttpStatus from 'common/utils/httpStatus'
import { Collection } from 'common/types/entities/api/specialEvent'
import { Error } from 'common/types/entities/error'
import history from 'common/history'
import { getSubVertical } from 'common/types/entities/mongo/channel'
import { includeStoreInfoWithHighRatingReview, SubVertical, verticalConfig } from 'common/constants'
import { getRecommendEventPageSize, usingPageBySpecialEventType } from 'common/constants/event'

const initialState: EventDetailState = {
  event: null,
  collections: [],
  recommendEvents: [],
  cacheParam: null,
}

const FETCH = '/event/specialEventDetail/FETCH'
const SET_PRODUCT_ZZIM = 'event/specialEventDetail/SET_PRODUCT_ZZIM'

export const fetch = (
  curState: StateKey,
  detailParam: EventDetailParam,
  subVertical: SubVertical,
  headers?: any,
) => async (dispatch, getState: () => CommonState) => {
  const cacheParam = createCacheParam(FETCH, 'fetch', { curState, detailParam })

  if (isCacheHit(getState().specialEventDetail[curState], cacheParam)) {
    return
  }

  const event = await fetchSpecialEvent(detailParam)

  const recommendsParam = {
    vertical: event.vertical,
    subVertical: verticalConfig[event.vertical].subVerticals.length > 1 ? event.subVertical : null,
    specialEventType: usingPageBySpecialEventType(subVertical) ? event.eventType : null,
    page: 1,
    pageSize: getRecommendEventPageSize() + 1,
  }

  const [collections, recommendResponse] = await Promise.all([
    fetchCollections(detailParam),
    fetchSpecialEvents(recommendsParam),
  ])

  if (event && ArrayUtils.isNotEmpty(collections)) {
    await Promise.all(
      collections
        .filter(collection => ArrayUtils.isNotEmpty(collection.productIds))
        .map(async collection => {
          const productParam: ProductParam = {
            subVertical,
            page: 1,
            eventId: detailParam.eventId,
            productIds: collection.productIds,
            pageSize: collection.productIds.length,
            includeBrandInfo: subVertical === SubVertical.ART,
            includeStoreCardInfo: subVertical === SubVertical.FOREIGN,
            includeStoreInfoWithHighRatingReview: includeStoreInfoWithHighRatingReview(subVertical),
          }
          const fetchedProducts = await fetchProductsByIds(productParam)
          collection.products = fetchedProducts.products.filter(
            product => getSubVertical(product.channel) === detailParam.subVertical,
          )

          let zzimProducts: ZzimProducts = null
          zzimProducts = await fetchZzimedProducts(collection.products.map(item => item._id), headers)

          if (zzimProducts) {
            collection.products.forEach(product => {
              const zzim = zzimProducts[Number(product._id)]
              product.zzimCount = zzim && zzim.count
              product.isMyZzim = (zzim && zzim.zzim) || false
            })
          }
        }),
    )
  }

  const recommendEvents =
    recommendResponse &&
    recommendResponse.events.filter(specialEvent => specialEvent.id !== event.id).slice(0, getRecommendEventPageSize())

  const result = {
    event,
    collections,
    recommendEvents,
  }

  dispatch({
    curState,
    cacheParam,
    type: FETCH,
    payload: result || initialState,
  })
}

export const toggleProductZzim = (curState: StateKey, productId: string, zzimed: boolean) => async dispatch => {
  const responseData = await setProductZzim(productId, zzimed)

  if (!responseData.bSuccess) {
    const error: Error = {
      status: responseData.sErrorCode === 'ACCESS_DENIED' ? HttpStatus.UNAUTHORIZED : HttpStatus.OK,
      message: responseData.htReturnValue.sMessage,
    }
    throw error
  }

  dispatch({
    curState,
    productId,
    type: SET_PRODUCT_ZZIM,
  })
}

const updateProductZzim = (collections: Collection[], productId: string) => {
  collections
    .filter(collection => ArrayUtils.isNotEmpty(collection.products))
    .forEach(collection => {
      const index = collection.products.findIndex(product => product._id === productId)
      const zzimProduct = collection.products[index]

      if (zzimProduct) {
        collection.products[index].isMyZzim = !zzimProduct.isMyZzim
      }
    })

  return collections
}

export const getEventsFromHistoryState = () => {
  if (history.location && history.location.state && history.location.state.eventDetail) {
    return {
      ...history.location.state.eventDetail,
    }
  }
}

const specialEventReducer = (state = initialState, action) => {
  switch (action.type) {
    case FETCH: {
      return {
        ...action.payload,
        cacheParam: action.cacheParam,
      }
    }
    case SET_PRODUCT_ZZIM:
      return {
        ...state,
        collections: updateProductZzim(state.collections, action.productId),
      }
    default:
      return state
  }
}

export default specialEventReducer
